import * as THREE from "three";
import {
  CircleStairsType,
  CircleSymbolType,
  PolygonSymbolType,
  RectStairsType,
  SingleLineType,
  SquareSymbolType,
  SvgType,
  SymbolType,
  TriangleSymbolType,
  WallShapeType,
  WallType,
  isAreaFillSymbolType,
  isAreaSymbolType,
  isBackdropImageType,
  isCircleStairsType,
  isCircleSymbolType,
  isDoorType,
  isSlidingDoorType,
  isDoubleDoorType,
  isPolygonSymbolType,
  isRectStairsType,
  isTurnStairsType,
  isSquareSymbolType,
  isBookShelfType,
  isSmartTableType,
  isSvgType,
  isTextType,
  isTriangleSymbolType,
  isVoidType,
  isWindowType,
} from "../types/floorplannerTypes";
import { resolveSmartTableLayout } from "../features/floorplan3d/symbols/dining/smartTableLayout";
import { resolveBookShelfLayout } from "../features/floorplan3d/symbols/livingRoom/bookShelfLayout";
import { getTurnStairsPlanBounds } from "../features/floorplan3d/symbols/primitives/stairs/stairGeometry";
import { getQuadraticBezierPoint } from "./arcUtils";
import {
  findFurthestPlaceablePosition,
  polygonsOverlap,
  resolveOppositeWallSnapPosition,
  wallCollisionBlocksSymbolFootprint,
} from "../features/floorplan3d/interaction/symbol3DDragCollision";

const EPSILON = 1e-8;
const MIN_FOOTPRINT_SIZE = 0.04;
const ARC_LINE_COLLISION_SEGMENTS = 24;

export type Symbol2DDragStructureInput = {
  walls: readonly WallType[];
  singleLines: readonly SingleLineType[];
  wallShapesById?: Map<string, WallShapeType[]>;
  defaultWallWidth: number;
  defaultSingleLineWidth: number;
  wallSnapTolerance?: number;
};

export type Symbol2DDragCollisionInput = Symbol2DDragStructureInput & {
  symbol: SymbolType;
  candidatePosition: THREE.Vector2;
  wallSnapReferencePosition?: THREE.Vector2;
};

export type Symbol2DDragResolutionInput = Symbol2DDragCollisionInput & {
  lastValidPosition: THREE.Vector2;
  bypassCollision?: boolean;
};

type CollisionStructure =
  | { kind: "wall"; wall: WallType }
  | { kind: "singleLine"; line: SingleLineType };

export function isLoose2DDragCollisionSymbol(symbol: SymbolType): boolean {
  return !(
    isDoorType(symbol) ||
    isSlidingDoorType(symbol) ||
    isDoubleDoorType(symbol) ||
    isWindowType(symbol) ||
    isVoidType(symbol) ||
    isAreaSymbolType(symbol) ||
    isAreaFillSymbolType(symbol) ||
    isBackdropImageType(symbol) ||
    isTextType(symbol)
  );
}

/**
 * Facade/elevation library symbols are meant to sit on elevation drawings
 * (single lines). Colliding with those lines makes trees and similar props
 * feel "superglued" next to elevations.
 */
export function shouldCollideWithSingleLines(symbol: SymbolType): boolean {
  if (isSvgType(symbol)) {
    const libraryKey = (symbol as SvgType).libraryKey;
    if (typeof libraryKey === "string" && libraryKey.startsWith("ELEVATION/")) {
      return false;
    }
  }
  return true;
}

export function canPlaceSymbol2DAtPosition(
  input: Symbol2DDragCollisionInput,
): boolean {
  const symbolFootprint = createSymbol2DFootprintPolygon(
    input.symbol,
    input.candidatePosition,
  );
  if (!symbolFootprint) return true;

  const wallCollision = input.walls.some((wall) => {
    return wallCollisionBlocksSymbolFootprint(
      wall,
      symbolFootprint,
      input.defaultWallWidth,
      {
        wallShapes: input.wallShapesById?.get(wall.id),
        wallSnapTolerance: input.wallSnapTolerance,
      },
    );
  });
  if (wallCollision) return false;

  if (!shouldCollideWithSingleLines(input.symbol)) {
    return true;
  }

  return !input.singleLines.some((line) => {
    const linePolygons = createSingleLineCollisionPolygons(
      line,
      input.defaultSingleLineWidth,
    );
    return linePolygons.some((linePolygon) =>
      polygonsOverlap(symbolFootprint, linePolygon),
    );
  });
}

export function resolveSymbol2DDragPosition(
  input: Symbol2DDragResolutionInput,
): THREE.Vector2 {
  if (input.bypassCollision) {
    return input.candidatePosition.clone();
  }

  const wallSnapReferencePosition =
    input.wallSnapReferencePosition ?? input.lastValidPosition;
  const dragInput = { ...input, wallSnapReferencePosition };

  if (canPlaceSymbol2DAtPosition(dragInput)) {
    return input.candidatePosition.clone();
  }

  const canPlaceAt = (candidatePosition: THREE.Vector2) =>
    canPlaceSymbol2DAtPosition({ ...dragInput, candidatePosition });

  // Ignore both walls and elevation lines when deciding whether a stuck
  // symbol may leave an already-invalid start pose. Previously only walls
  // were ignored, so symbols overlapping single lines (elevation drawings)
  // could never escape and felt glued in place.
  const canPlaceAtIgnoringStructures = (candidatePosition: THREE.Vector2) =>
    canPlaceSymbol2DAtPosition({
      ...dragInput,
      candidatePosition,
      walls: [],
      singleLines: [],
    });

  const symbolFootprint = createSymbol2DFootprintPolygon(
    input.symbol,
    input.candidatePosition,
  );
  if (symbolFootprint) {
    const oppositeSnapPosition = resolveOppositeWallSnapPosition({
      walls: input.walls,
      symbolFootprint,
      candidatePosition: input.candidatePosition,
      defaultWallWidth: input.defaultWallWidth,
      wallShapesById: input.wallShapesById,
      wallSnapTolerance: input.wallSnapTolerance,
      wallSnapReferencePosition,
      canPlaceAt,
    });
    if (oppositeSnapPosition) {
      return oppositeSnapPosition;
    }
  }

  if (
    !canPlaceAt(input.lastValidPosition) &&
    canPlaceAtIgnoringStructures(input.candidatePosition)
  ) {
    return input.candidatePosition.clone();
  }

  // The pointer jumped from a valid position straight into a structure. Find the
  // actual contact point along the movement segment so we hug the boundary
  // regardless of how large the jump was (slow drag vs. fast drag / slow machine).
  const contactPosition = findFurthestPlaceablePosition(
    input.lastValidPosition,
    input.candidatePosition,
    canPlaceAt,
  );

  const collidingStructure = findClosestCollidingStructure(dragInput);
  if (collidingStructure) {
    const tangent = getStructureTangentNearPoint(
      collidingStructure,
      contactPosition,
    );
    // Project the remaining (blocked) movement onto the structure and slide
    // along it starting from the contact point, so the symbol keeps moving
    // parallel to the wall while staying pressed against it.
    const delta = input.candidatePosition.clone().sub(contactPosition);
    const slideDistance = delta.dot(tangent);
    if (Math.abs(slideDistance) > EPSILON) {
      const slideTarget = contactPosition
        .clone()
        .add(tangent.multiplyScalar(slideDistance));
      if (canPlaceAt(slideTarget)) {
        return slideTarget;
      }
      // Sliding ran into another structure (e.g. a corner); advance as far as
      // possible along the slide direction instead of giving up.
      const slideContact = findFurthestPlaceablePosition(
        contactPosition,
        slideTarget,
        canPlaceAt,
      );
      if (slideContact.distanceToSquared(contactPosition) > EPSILON) {
        return slideContact;
      }
    }
  }

  const axisCandidates = [
    new THREE.Vector2(input.candidatePosition.x, contactPosition.y),
    new THREE.Vector2(contactPosition.x, input.candidatePosition.y),
  ].sort(
    (a, b) =>
      b.distanceToSquared(contactPosition) -
      a.distanceToSquared(contactPosition),
  );

  const validAxisCandidate = axisCandidates.find((candidatePosition) =>
    canPlaceAt(candidatePosition),
  );

  return validAxisCandidate?.clone() ?? contactPosition.clone();
}

export function resolveSymbolGroup2DDragOffset(
  symbols: SymbolType[],
  originalPositions: Map<string, { position?: THREE.Vector2 }>,
  candidateOffset: THREE.Vector2,
  lastValidOffset: THREE.Vector2,
  structures: Symbol2DDragStructureInput,
  options: { bypassCollision?: boolean } = {},
): THREE.Vector2 {
  if (options.bypassCollision) {
    return candidateOffset.clone();
  }

  let resolvedOffset = candidateOffset.clone();

  for (const symbol of symbols) {
    const originalPosition = originalPositions.get(symbol.id)?.position;
    if (!originalPosition || !isLoose2DDragCollisionSymbol(symbol)) continue;

    const candidatePosition = originalPosition.clone().add(resolvedOffset);
    const lastValidPosition = originalPosition.clone().add(lastValidOffset);
    const resolvedPosition = resolveSymbol2DDragPosition({
      ...structures,
      symbol,
      candidatePosition,
      lastValidPosition,
      wallSnapReferencePosition: originalPosition,
    });

    resolvedOffset = resolvedPosition.sub(originalPosition);
    if (
      !canPlaceSymbolsAtOffset(
        symbols,
        originalPositions,
        resolvedOffset,
        structures,
      )
    ) {
      return lastValidOffset.clone();
    }
  }

  return resolvedOffset;
}

export function createSymbol2DFootprintPolygon(
  symbol: SymbolType,
  position = symbol.position,
): THREE.Vector2[] | null {
  const dimensions = getSymbol2DFootprint(symbol);
  if (!dimensions) return null;

  const halfWidth = dimensions.width / 2;
  const halfHeight = dimensions.height / 2;
  const centerX = dimensions.centerX ?? 0;
  const centerY = dimensions.centerY ?? 0;
  const rotation = symbol.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const corners = [
    new THREE.Vector2(centerX - halfWidth, centerY - halfHeight),
    new THREE.Vector2(centerX + halfWidth, centerY - halfHeight),
    new THREE.Vector2(centerX + halfWidth, centerY + halfHeight),
    new THREE.Vector2(centerX - halfWidth, centerY + halfHeight),
  ];

  return corners.map(
    (corner) =>
      new THREE.Vector2(
        position.x + corner.x * cos - corner.y * sin,
        position.y + corner.x * sin + corner.y * cos,
      ),
  );
}

function canPlaceSymbolsAtOffset(
  symbols: SymbolType[],
  originalPositions: Map<string, { position?: THREE.Vector2 }>,
  offset: THREE.Vector2,
  structures: Symbol2DDragStructureInput,
): boolean {
  return symbols.every((symbol) => {
    const originalPosition = originalPositions.get(symbol.id)?.position;
    if (!originalPosition || !isLoose2DDragCollisionSymbol(symbol)) return true;

    return canPlaceSymbol2DAtPosition({
      ...structures,
      symbol,
      candidatePosition: originalPosition.clone().add(offset),
      wallSnapReferencePosition: originalPosition,
    });
  });
}

function getSymbol2DFootprint(
  symbol: SymbolType,
): { width: number; height: number; centerX?: number; centerY?: number } | null {
  let width: number | undefined;
  let height: number | undefined;
  let centerX: number | undefined;
  let centerY: number | undefined;

  if (isSvgType(symbol)) {
    width = (symbol as SvgType).svgLength;
    height = (symbol as SvgType).svgHeight;
  } else if (isSmartTableType(symbol)) {
    const bounds = resolveSmartTableLayout(symbol.smartTable).bounds;
    width = bounds.width;
    height = bounds.height;
    centerX = (bounds.minX + bounds.maxX) / 2;
    centerY = (bounds.minY + bounds.maxY) / 2;
  } else if (isBookShelfType(symbol)) {
    const bounds = resolveBookShelfLayout(symbol.bookShelf).bounds;
    width = bounds.width;
    height = bounds.depth;
  } else if (isSquareSymbolType(symbol)) {
    width = (symbol as SquareSymbolType).squareWidth;
    height = (symbol as SquareSymbolType).squareHeight;
  } else if (isTriangleSymbolType(symbol)) {
    width = (symbol as TriangleSymbolType).triangleWidth;
    height = (symbol as TriangleSymbolType).triangleHeight;
  } else if (isCircleSymbolType(symbol)) {
    width = (symbol as CircleSymbolType).circleWidth;
    height = (symbol as CircleSymbolType).circleHeight;
  } else if (isPolygonSymbolType(symbol)) {
    width = (symbol as PolygonSymbolType).polygonWidth;
    height = (symbol as PolygonSymbolType).polygonHeight;
  } else if (isRectStairsType(symbol)) {
    width = (symbol as RectStairsType).rectStairsWidth;
    height = (symbol as RectStairsType).rectStairsHeight;
  } else if (isTurnStairsType(symbol)) {
    const bounds = getTurnStairsPlanBounds(symbol, {
      defaultStepSize: symbol.stairStepSize ?? 0.32,
    });
    width = bounds.width;
    height = bounds.depth;
  } else if (isCircleStairsType(symbol)) {
    width = (symbol as CircleStairsType).circleStairsWidth;
    height = (symbol as CircleStairsType).circleStairsWidth;
  } else {
    width = (symbol as any).width;
    height = (symbol as any).height;
  }

  if (!isPositiveFiniteNumber(width) || !isPositiveFiniteNumber(height)) {
    return null;
  }

  return {
    width: Math.max(width, MIN_FOOTPRINT_SIZE),
    height: Math.max(height, MIN_FOOTPRINT_SIZE),
    centerX,
    centerY,
  };
}

function findClosestCollidingStructure(
  input: Symbol2DDragCollisionInput,
): CollisionStructure | null {
  const symbolFootprint = createSymbol2DFootprintPolygon(
    input.symbol,
    input.candidatePosition,
  );
  if (!symbolFootprint) return null;

  let closestStructure: CollisionStructure | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  input.walls.forEach((wall) => {
    if (
      !wallCollisionBlocksSymbolFootprint(
        wall,
        symbolFootprint,
        input.defaultWallWidth,
        {
          wallShapes: input.wallShapesById?.get(wall.id),
          wallSnapTolerance: input.wallSnapTolerance,
        },
      )
    ) {
      return;
    }

    const distance = distanceToObjectCenterline(input.candidatePosition, wall);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestStructure = { kind: "wall", wall };
    }
  });

  if (shouldCollideWithSingleLines(input.symbol)) {
    input.singleLines.forEach((line) => {
      const linePolygons = createSingleLineCollisionPolygons(
        line,
        input.defaultSingleLineWidth,
      );
      if (
        !linePolygons.some((linePolygon) =>
          polygonsOverlap(symbolFootprint, linePolygon),
        )
      ) {
        return;
      }

      const distance = distanceToObjectCenterline(input.candidatePosition, line);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestStructure = { kind: "singleLine", line };
      }
    });
  }

  return closestStructure;
}

function getStructureTangentNearPoint(
  structure: CollisionStructure,
  point: THREE.Vector2,
): THREE.Vector2 {
  const object = structure.kind === "wall" ? structure.wall : structure.line;
  return getObjectTangentNearPoint(object, point);
}

function getObjectTangentNearPoint(
  object: WallType | SingleLineType,
  point: THREE.Vector2,
): THREE.Vector2 {
  if (object.lineForm === "arc" && object.controlPoint) {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index <= ARC_LINE_COLLISION_SEGMENTS; index += 1) {
      const t = index / ARC_LINE_COLLISION_SEGMENTS;
      const sample = getQuadraticBezierPoint(
        t,
        object.start,
        object.controlPoint,
        object.end,
      );
      const distance = sample.distanceToSquared(point);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    const previousT =
      Math.max(closestIndex - 1, 0) / ARC_LINE_COLLISION_SEGMENTS;
    const nextT =
      Math.min(closestIndex + 1, ARC_LINE_COLLISION_SEGMENTS) /
      ARC_LINE_COLLISION_SEGMENTS;
    const previous = getQuadraticBezierPoint(
      previousT,
      object.start,
      object.controlPoint,
      object.end,
    );
    const next = getQuadraticBezierPoint(
      nextT,
      object.start,
      object.controlPoint,
      object.end,
    );
    const tangent = next.sub(previous);
    if (tangent.lengthSq() > EPSILON) return tangent.normalize();
  }

  const tangent = object.end.clone().sub(object.start);
  if (tangent.lengthSq() <= EPSILON) return new THREE.Vector2(1, 0);
  return tangent.normalize();
}

function distanceToObjectCenterline(
  point: THREE.Vector2,
  object: WallType | SingleLineType,
): number {
  if (object.lineForm === "arc" && object.controlPoint) {
    let closestDistance = Number.POSITIVE_INFINITY;
    let previous = object.start.clone();
    for (let index = 1; index <= ARC_LINE_COLLISION_SEGMENTS; index += 1) {
      const t = index / ARC_LINE_COLLISION_SEGMENTS;
      const next = getQuadraticBezierPoint(
        t,
        object.start,
        object.controlPoint,
        object.end,
      );
      closestDistance = Math.min(
        closestDistance,
        distanceToSegment(point, previous, next),
      );
      previous = next;
    }
    return closestDistance;
  }

  return distanceToSegment(point, object.start, object.end);
}

function createSingleLineCollisionPolygons(
  line: SingleLineType,
  defaultSingleLineWidth: number,
): THREE.Vector2[][] {
  const lineWeightWidth = isPositiveFiniteNumber(line.lineWeight)
    ? line.lineWeight / 44
    : 0;
  const lineWidth = Math.max(defaultSingleLineWidth, lineWeightWidth, EPSILON);
  if (line.lineForm === "arc" && line.controlPoint) {
    return [createArcLineCollisionPolygon(line, lineWidth)].filter(
      (polygon) => polygon.length >= 3,
    );
  }

  return [createStraightLineCollisionPolygon(line, lineWidth)].filter(
    (polygon) => polygon.length >= 3,
  );
}

function createStraightLineCollisionPolygon(
  line: SingleLineType,
  lineWidth: number,
): THREE.Vector2[] {
  const direction = line.end.clone().sub(line.start);
  if (direction.lengthSq() <= EPSILON) return [];

  direction.normalize();
  const normal = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(
    lineWidth / 2,
  );

  return [
    line.start.clone().add(normal),
    line.end.clone().add(normal),
    line.end.clone().sub(normal),
    line.start.clone().sub(normal),
  ];
}

function createArcLineCollisionPolygon(
  line: SingleLineType,
  lineWidth: number,
): THREE.Vector2[] {
  if (!line.controlPoint) return [];

  const centerline: THREE.Vector2[] = [];
  for (let index = 0; index <= ARC_LINE_COLLISION_SEGMENTS; index += 1) {
    const t = index / ARC_LINE_COLLISION_SEGMENTS;
    centerline.push(
      getQuadraticBezierPoint(t, line.start, line.controlPoint, line.end),
    );
  }

  const left: THREE.Vector2[] = [];
  const right: THREE.Vector2[] = [];
  for (let index = 0; index < centerline.length; index += 1) {
    const previous = centerline[Math.max(index - 1, 0)];
    const next = centerline[Math.min(index + 1, centerline.length - 1)];
    const tangent = next.clone().sub(previous);
    if (tangent.lengthSq() <= EPSILON) continue;

    tangent.normalize();
    const normal = new THREE.Vector2(-tangent.y, tangent.x).multiplyScalar(
      lineWidth / 2,
    );
    left.push(centerline[index].clone().add(normal));
    right.push(centerline[index].clone().sub(normal));
  }

  return left.concat(right.reverse());
}

function distanceToSegment(
  point: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
): number {
  const segment = end.clone().sub(start);
  const lengthSq = segment.lengthSq();
  if (lengthSq <= EPSILON) return point.distanceTo(start);

  const t = THREE.MathUtils.clamp(
    point.clone().sub(start).dot(segment) / lengthSq,
    0,
    1,
  );
  return point.distanceTo(start.clone().add(segment.multiplyScalar(t)));
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}
